/**
 * One lead source in detail: Kate's note, the pass log (the documentation
 * that the list is worked) and the organisations she proposed from it.
 * Presentational — the page owns the detail query.
 */
import type { LeadSourceDetail } from "../../api/leadSources";
import { formatRelative } from "../../lib/formatRelative";
import { blockedSentence, reviewTaskLabel, sourceStatusPill } from "../../lib/leadSources";
import { Drawer } from "../admin/Drawer";
import { Pill } from "../Pill";

export function SourceDrawer({
  open,
  detail,
  loading,
  onClose,
}: {
  open: boolean;
  detail: LeadSourceDetail | undefined;
  loading: boolean;
  onClose: () => void;
}) {
  const pill = detail ? sourceStatusPill(detail) : null;
  return (
    <Drawer open={open} onClose={onClose} eyebrow="Lead source" title={detail?.name ?? "…"}>
      {loading && !detail ? (
        <div className="text-sm text-ink-low">Loading…</div>
      ) : detail ? (
        <div className="space-y-6 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            {pill ? <Pill value={pill.label} tone={pill.tone} /> : null}
            <span className="pill pill--muted">{detail.kind}</span>
            <span className="pill pill--muted">{detail.cadence}</span>
            {detail.url ? (
              <a href={detail.url} target="_blank" rel="noreferrer" className="link">
                open list ↗
              </a>
            ) : null}
          </div>

          {detail.status === "blocked" ? (
            <div className="panel panel--danger">
              <div className="font-semibold text-danger">
                {blockedSentence(detail.blocked_reason)}
              </div>
              <div className="text-ink-medium mt-1">
                Kate does not crawl this list – {reviewTaskLabel(detail)}.
              </div>
            </div>
          ) : null}

          {detail.note ? (
            <div>
              <span className="label">Kate's note</span>
              <p className="text-ink-medium">{detail.note}</p>
            </div>
          ) : null}

          <section>
            <span className="label">Passes</span>
            {detail.logs.length ? (
              <table className="table">
                <tbody>
                  {detail.logs.map((log) => (
                    <tr key={log.id}>
                      <td className="tnum text-ink-low whitespace-nowrap">
                        {log.worked_at?.slice(0, 10)}
                      </td>
                      <td className="text-ink-medium">{log.summary}</td>
                      <td className="num text-ink-low whitespace-nowrap">
                        {log.members_seen != null ? `${log.members_seen} seen` : ""}
                        {log.new_members ? ` · +${log.new_members}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-ink-low">No pass logged yet.</p>
            )}
          </section>

          <section>
            <span className="label">Proposed from this source</span>
            {detail.candidates.length ? (
              <ul className="space-y-1.5">
                {detail.candidates.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3">
                    <span>
                      <span className="font-semibold text-ink">{c.name}</span>{" "}
                      <span className="text-ink-low">{c.why}</span>
                    </span>
                    <span className="whitespace-nowrap">
                      <Pill value={c.status} />{" "}
                      <span className="text-xs text-ink-low">{formatRelative(c.created_at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-ink-low">Nothing proposed yet.</p>
            )}
          </section>
        </div>
      ) : (
        <div className="text-sm text-ink-low">Source not found.</div>
      )}
    </Drawer>
  );
}
